import React, { useEffect } from "react";
import styled from "styled-components";
import always from "ramda/src/always";
import propEq from "ramda/src/propEq";
import concat from "ramda/src/concat";

import { normalize } from "../../helpers/normalize";
import { useNetwork } from "../../hooks/useNetwork";
import { Select } from "../../UI/Select";
import { MatchingRoundCircle } from "../../components/MatchingRoundStatus";
import { SelectWrapper } from "../../components/SelectWrapper";

const L2SelectorWrapper = styled.div`

  .room-l2-selector__single-value {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  label {
    color: ${(props) => props.variant === "table" && "#FFF"};
  }
`;

const Labelcontainer = styled.div`
  opacity: ${(props) => props.status === "ARCHIVED"? 0.6 : 1};
  line-height: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  
`;

export const L2MultiSelector = ({ // temporary
  isDisabled = false,
  onSelectHandler,
  defaultValue = 0,
  fullWidth = false,
  isMulti = false,
  withStatus = true,
  isSearchable = true,
  extraMenuOptions = [],
  defaulOptionsValue = undefined,
  customLabel = "",
  resetDeps = [],
}) => {
  const [value, setValue] = React.useState(defaulOptionsValue ? defaulOptionsValue : undefined);
  const { getL2Options, selectedNetwork } = useNetwork();

  let l2Options =  getL2Options(selectedNetwork.l1Id).map((x) => ({ value: x.l2Id, label: x.l2Name, status: x.matchingRoundStatus }));

  if (extraMenuOptions.length > 0) {
    l2Options = concat(extraMenuOptions, l2Options);
  }

  
  l2Options = [
    l2Options.filter(x => x.status === "UPCOMING"),
    l2Options.filter(x => x.status === "MATCHING"),
    l2Options.filter(x => x.status === "ACTIVE"),
    l2Options.filter(x => x.status === "CLOSED"),
    l2Options.filter(x => x.status === "ARCHIVED")
  ].flat();

  useEffect(() => {
    onSelectHandler(null);
    setValue("");
  }, [selectedNetwork.l1Id, normalize(resetDeps)]);

  const handleChange = (value) => {
    setValue(value);
    if(isMulti) {
      onSelectHandler(value);
    } else {
      onSelectHandler(value.value);
    }
    
  };

  useEffect(() => {
    const option = l2Options.find(propEq("value", defaultValue));
    if (!option) return;
    setValue(option);
    // onSelectHandler(option);
  }, [defaultValue, normalize(l2Options)]);

  const CustomLabel = ({ label }) => (<div>{label}</div>);
  return (
      <L2SelectorWrapper>
      <Select
        key="subnetwork-multiselect"
        fullWidth={fullWidth}
        value={value}
        label={customLabel ? customLabel : "Matching Round"} 
        options={l2Options}
        isMulti={isMulti}
        isDisabled={isDisabled}
        isSearchable={isSearchable}
        inputId="subnetwork-select"
        className="l2-selector"
        classNamePrefix="room-l2-selector"
        onChange={handleChange}
        noOptionsMessage={always(selectedNetwork.l1Id ? "Empty" : "Please select network")}
        formatOptionLabel={CustomLabel}
      />
      </L2SelectorWrapper>
  );
};

export const L2Selector = ({
  isDisabled = false,
  onSelectHandler,
  defaultValue = 0,
  fullWidth = false,
  isSearchable = true,
  extraMenuOptions = [],
  filter = (x) => true,
  resetDeps = [],
  variant= "default",
}) => {
  const [value, setValue] = React.useState();
  const { getL2Options, selectedNetwork } = useNetwork();

  let l2Options = getL2Options(selectedNetwork.l1Id)
    .filter((x) => filter(x))
    .map((x) => ({ value: x.l2Id, label: x.l2Name, status: x.matchingRoundStatus }));

  if (extraMenuOptions.length > 0) {
    l2Options = concat(extraMenuOptions, l2Options);
  }

  
  l2Options = [
    extraMenuOptions,
    l2Options.filter(x => x.status === "UPCOMING"),
    l2Options.filter(x => x.status === "MATCHING"),
    l2Options.filter(x => x.status === "ACTIVE"),
    l2Options.filter(x => x.status === "CLOSED"),
    l2Options.filter(x => x.status === "ARCHIVED")
  ].flat();

  useEffect(() => {
    onSelectHandler(null);
    setValue("");
  }, [selectedNetwork.l1Id, normalize(resetDeps)]);

  const handleChange = (value) => {
    setValue(value);
    onSelectHandler(value.value);
  };

  useEffect(() => {
    const option = l2Options.find(propEq("value", defaultValue));
    if (!option) return;
    setValue(option);
    // onSelectHandler(option);
  }, [defaultValue, normalize(l2Options)]);

  const CustomLabel = ({ label, status }) => 
    <Labelcontainer status={status}>
        <div>
          {label}
        </div>
        <div>
          {status && <MatchingRoundCircle status={status}/>}
        </div>
    </Labelcontainer>;

  return (
    <SelectWrapper>
      <L2SelectorWrapper variant={variant}>
      <Select
        fullWidth={fullWidth}
        value={value}
        label="Matching Round"
        options={l2Options}
        isDisabled={isDisabled}
        isSearchable={isSearchable}
        inputId="subnetwork-select"
        className="l2-selector"
        classNamePrefix="room-l2-selector"
        onChange={handleChange}
        noOptionsMessage={always(selectedNetwork.l1Id ? "Empty" : "Please select network")}
        formatOptionLabel={CustomLabel}
      />
      </L2SelectorWrapper>
    </SelectWrapper>
  );
};

export const L3Selector = ({
  onSelectHandler,
  l2Selection,
  isDisabled = false,
  defaultValue = 0,
  fullWidth = false,
  extraMenuOptions = [],
}) => {
  const [value, setValue] = React.useState("");
  const { selectedNetwork, getL3Options } = useNetwork();

  let l3Options = l2Selection
    ? getL3Options(selectedNetwork.l1Id, l2Selection).map((x) => ({
      value: x.l3Id,
      label: x.name,
    }))
    : [];

  if (extraMenuOptions.length > 0) {
    l3Options = concat(extraMenuOptions, l3Options);
  }

  useEffect(() => {
    setValue("");
    onSelectHandler(null);
  }, [selectedNetwork.l1Id, l2Selection]);

  const handleChange = (value) => {
    setValue(value);
    onSelectHandler(value.value);
  };

  useEffect(() => {
    if (!defaultValue || !l3Options.length) return;
    setValue(defaultValue);
    onSelectHandler(defaultValue);
  }, [defaultValue, normalize(l3Options)]);

  return (
    <SelectWrapper>
      <Select
        fullWidth={fullWidth}
        value={value}
        label="Unit Type"
        inputId="unit-type-select"
        options={l3Options}
        isDisabled={isDisabled}
        onChange={handleChange}
        noOptionsMessage={always(l2Selection ? "Empty" : "Please select subnetwork")}
      />
    </SelectWrapper>
  );
};

export const L4Selector = ({
  onSelectHandler,
  l2Selection,
  l3Selection,
  isDisabled = false,
  defaultValue = 0,
  fullWidth = false,
}) => {
  const [value, setValue] = React.useState("");
  const { selectedNetwork, getL3 } = useNetwork();

  const l4Options = l2Selection
    ? getL3(selectedNetwork.l1Id, l2Selection, l3Selection).l4.map((x) => ({
      value: x.l4Id,
      label: x.name,
    }))
    : [];

  const handleChange = (value) => {
    setValue(value);
    onSelectHandler(value.value);
  };

  useEffect(() => {
    if (!defaultValue || !l4Options.length) return;
    setValue(defaultValue);
    onSelectHandler(defaultValue);
  }, [defaultValue, normalize(l4Options)]);

  return (
    <SelectWrapper>
      <Select
        value={value}
        label="Room Type"
        inputId="room-type-select"
        fullWidth={fullWidth}
        options={l4Options}
        isDisabled={isDisabled}
        onChange={handleChange}
        noOptionsMessage={always(l2Selection ? "Empty" : "Please select unit type")}
      />
    </SelectWrapper>
  );
};
